import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import styled from "@emotion/styled";
import React from "react";
import { match } from "ts-pattern";
import { default as SendIcon } from "../../../../public/assets/chat-send-icon.svg?react";
import { default as PaperclipIcon } from "../../../../public/assets/paperclip.svg?react";
import { useAutosizeTextarea } from "../../../hooks/useAutoresize";
import useOpen from "../../../hooks/useOpen";
import { usePermissionDeniedModal } from "../../../hooks/usePermissionDeniedModal";
import {
  CaregiverCommCenterMessage,
  CommCenterMessageActionId,
  CommCenterTicket,
  CommCenterTicketId,
} from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import ChooseAttachmentBottomSheet from "../../ChooseAttachmentBottomSheet";
import Flex from "../../Generic/Flex";
import { InfoCard } from "../../Generic/InfoCard";
import { CommCenterListActionItem, getLastMessageListPayload } from "../utils/chatUtils";
import ChatMessageList from "./ChatMessageList";
import ChatRoomAttachmentsBar from "./ChatRoomAttachmentsBar";

interface Props {
  ticket: CommCenterTicket;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  images: string[];
  documents: string[];
  videos: string[];
  onSendMessage: (params: {
    ticketId: CommCenterTicketId;
    message: CaregiverCommCenterMessage;
  }) => void;
  onSubmitChatMessage: (message: string) => void;
  onClickActionItem: (item: CommCenterListActionItem, actionId: CommCenterMessageActionId) => void;
  onSelectImages: (images: string[]) => void;
  onSelectDocuments: (documents: string[]) => void;
  onSelectVideos: (videos: string[]) => void;
  onClickRemoveImage: (image: string) => void;
  onClickRemoveDocument: (document: string) => void;
  onClickRemoveVideo: (document: string) => void;
}

function ChatRoomFooter(props: Props) {
  const { openModal: openPermissionDeniedModal } = usePermissionDeniedModal();

  const handleClick = () => {
    const textarea = props.inputRef.current;

    if (textarea !== null) {
      props.onSubmitChatMessage(textarea.value);
    }
  };

  const bottomSheet = useOpen();
  useAutosizeTextarea(props.inputRef);

  const handleClickCamera = async () => {
    const { camera: cameraPermission } = await Camera.checkPermissions();

    if (cameraPermission === "denied") {
      bottomSheet.actions.dismiss();
      openPermissionDeniedModal("camera");
      return;
    }

    const photo = await Camera.getPhoto({
      source: CameraSource.Camera,
      resultType: CameraResultType.Uri,
      correctOrientation: true,
      quality: 90,
    });

    bottomSheet.actions.dismiss();

    props.onSelectImages([photo.webPath as string]);
  };

  const handleClickGallery = async () => {
    const { photos: photosPermission } = await Camera.checkPermissions();

    if (photosPermission === "denied") {
      bottomSheet.actions.dismiss();
      openPermissionDeniedModal("photos");
      return;
    }

    const { photos } = await Camera.pickImages({
      width: 1024,
      height: 1024,
      correctOrientation: true,
      quality: 90,
      limit: 5,
    });

    bottomSheet.actions.dismiss();

    props.onSelectImages(photos.slice(0, 5).map((photo) => photo.webPath));
  };

  const handleSelectFile = async (files: FileList) => {
    props.onSelectDocuments([...files].map((file) => URL.createObjectURL(file)));

    bottomSheet.actions.dismiss();
  };

  const handleSelectVideo = async (files: FileList) => {
    props.onSelectVideos([...files].map((file) => URL.createObjectURL(file)));

    bottomSheet.actions.dismiss();
  };

  if (props.ticket.status === "RESOLVED") {
    return (
      <InfoCard style={{ textAlign: "center" }}>
        {translate(STRINGS.THIS_CONVERSATION_HAS_BEEN_RESOLVED)}
      </InfoCard>
    );
  }

  if (props.ticket.enableAgencyResponse) {
    return (
      <>
        <ChatRoomFooter.InputForm>
          <ChatRoomFooter.InputButton
            variant="secondary"
            type="button"
            onClick={bottomSheet.actions.open}
          >
            <PaperclipIcon width={24} height={24} />
          </ChatRoomFooter.InputButton>
          <ChatRoomFooter.FakeInput>
            <ChatRoomAttachmentsBar
              images={props.images}
              documents={props.documents}
              videos={props.videos}
              onClickRemoveImage={props.onClickRemoveImage}
              onClickRemoveDocument={props.onClickRemoveDocument}
              onClickRemoveVideo={props.onClickRemoveVideo}
            />
            <Flex direction="row" alignItems="stretch" justifyContent="stetch">
              <ChatRoomFooter.InputWrapper>
                <ChatRoomFooter.TextBox
                  ref={props.inputRef}
                  rows={1}
                  placeholder={translate(STRINGS.TYPE_MESSAGE) + "..."}
                />
              </ChatRoomFooter.InputWrapper>
            </Flex>
          </ChatRoomFooter.FakeInput>
          <ChatRoomFooter.InputButton variant="primary" onClick={handleClick}>
            <SendIcon />
          </ChatRoomFooter.InputButton>
        </ChatRoomFooter.InputForm>
        <ChooseAttachmentBottomSheet
          isOpen={bottomSheet.isOpen}
          onClickCamera={handleClickCamera}
          onClickGallery={handleClickGallery}
          onSelectFile={handleSelectFile}
          onSelectVideo={handleSelectVideo}
          onRequestClose={bottomSheet.actions.dismiss}
        />
      </>
    );
  }

  const payload = getLastMessageListPayload(props.ticket);

  if (payload === null || payload.payload.items.length === 0) {
    return null;
  }

  return (
    <ChatRoomFooter.LabelsWrapper>
      <ChatMessageList
        list={payload.payload}
        ticketId={props.ticket.id}
        actionId={payload.actionId}
        isLatestMessage={true}
        onClickActionItem={props.onClickActionItem}
      />
    </ChatRoomFooter.LabelsWrapper>
  );
}

ChatRoomFooter.InputButton = styled.button<{ variant: "primary" | "secondary" }>`
  --background-color: ${(props) =>
    match(props.variant)
      .with("primary", () => "var(--primary-color)")
      .with("secondary", () => "var(--gray-100)")
      .exhaustive()};

  --color: ${(props) =>
    match(props.variant)
      .with("primary", () => "white")
      .with("secondary", () => "var(--gray-700)")
      .exhaustive()};

  background-color: var(--background-color);
  border-radius: 12px;
  width: var(--form-height);
  height: var(--form-height);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;

  svg {
    color: var(--color);
  }
`;

ChatRoomFooter.InputForm = styled.div`
  --form-height: 40px;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 8px;
  padding: 14px 12px;
  background-color: white;
`;

ChatRoomFooter.InputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

ChatRoomFooter.FakeInput = styled.div`
  flex: 1;
  background: #f4f7fb;
  border-radius: 16px;
`;

ChatRoomFooter.TextBox = styled.textarea`
  flex: 1;
  border: none;
  background: none;
  color: #283847;
  resize: none;
  overflow: hidden;
  line-height: 1.4;
  font-size: inherit;
  min-height: var(--form-height);
  padding: 8px 12px;
  box-sizing: border-box;

  ::placeholder {
    color: #8f969f;
  }
`;

ChatRoomFooter.LabelsWrapper = styled.div`
  background-color: var(--scaffold-background);
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 16px;

  ${ChatMessageList.Item} {
    box-shadow: 0px 16px 10px rgba(0, 0, 0, 0.02);
  }

  ${ChatMessageList.Root} {
    justify-content: flex-end;
  }
`;

export default ChatRoomFooter;
